"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { Text } from "@leabankab/component-library";
import { focusFirstErrorAfterRender } from "@/components/form";
import { AddDebtButton } from "@/features/refinance/components/debt-collection/add-debt-button";
import { isAddedDebtSaved } from "../hooks/use-confirm-debt-form";
import { useSaveAddedDebt } from "../hooks/use-save-added-debt";
import { AddedDebtFormItem } from "../types";
import { emptyAddedDebt } from "../utils/confirm-debt-payload";
import type { DebtType } from "../utils/debt-types";
import { AddedDebtCard } from "./added-debt-card";

interface AddedDebtListProps {
	allowedTypes: readonly DebtType[];
}

export const AddedDebtList = ({ allowedTypes }: AddedDebtListProps) => {
	const t = useTranslations("pages.ConfirmDebtPage.added");
	const { control } = useFormContext();
	const { fields, append, remove } = useFieldArray({
		control,
		name: "addedDebts",
	});
	const debts = useWatch({ control, name: "addedDebts" }) as
		AddedDebtFormItem[] | undefined;

	const [expandIndex, setExpandIndex] = useState<number | null>(null);
	const [revealAttempt, setRevealAttempt] = useState(0);
	const focusAfterRemove = useRef(false);
	const listRef = useRef<HTMLDivElement>(null);
	const addButtonRef = useRef<HTMLButtonElement>(null);
	const saveDebt = useSaveAddedDebt();

	const unsaved = (debts ?? []).flatMap((debt, index) =>
		isAddedDebtSaved(debt, allowedTypes) ? [] : [index]
	);

	// The removed card took focus with it.
	useEffect(() => {
		if (!focusAfterRemove.current) return;
		focusAfterRemove.current = false;

		addButtonRef.current?.focus();
	});

	// An unsaved debt is saved first, or shows what it still needs.
	const handleAdd = async () => {
		const saved = await Promise.all(unsaved.map(saveDebt));
		if (saved.includes(false)) {
			setRevealAttempt((attempt) => attempt + 1);
			focusFirstErrorAfterRender(listRef);
			return;
		}

		setExpandIndex(fields.length);
		append(emptyAddedDebt());
	};

	const handleRemove = (index: number) => {
		setExpandIndex(null);
		focusAfterRemove.current = true;
		remove(index);
	};

	return (
		<div ref={listRef} className="flex flex-col gap-1.5 pt-1">
			{fields.map((field, index) => (
				<AddedDebtCard
					key={field.id}
					index={index}
					allowedTypes={allowedTypes}
					defaultExpanded={index === expandIndex}
					revealAttempt={revealAttempt}
					onRemove={() => handleRemove(index)}
				/>
			))}

			<AddDebtButton
				ref={addButtonRef}
				title={t("addButton")}
				onClick={() => void handleAdd()}
			/>

			<div role="status">
				{unsaved.length > 0 && (
					<Text textStyle="body-sm" color="text.subtle">
						{t("saveBeforeAdding")}
					</Text>
				)}
			</div>
		</div>
	);
};
